import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Monitor } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// Ubicaciones que corresponden a infraestructura cloud/virtual (solo para servers)
const CLOUD_LOCATION_NAMES = [
  'Amazon Web Services', 'Microsoft Azure', 'Google Cloud Platform',
  'VMware vSphere', 'Hyper-V', 'KVM / Proxmox', 'VirtualBox',
  'Xen', 'Contenedores / Docker', 'DigitalOcean',
];

// Tipos de dispositivo considerados "servidor/virtual"
const SERVER_TYPES = ['server'];

const DEVICE_TYPES = [
  { value: 'workstation',  label: 'Workstation',   icon: '🖥' },
  { value: 'laptop',       label: 'Laptop',        icon: '💻' },
  { value: 'server',       label: 'Server',        icon: '🖧' },
  { value: 'printer',      label: 'Impresora',     icon: '🖨' },
  { value: 'switch',       label: 'Switch',        icon: '🔀' },
  { value: 'router',       label: 'Router',        icon: '📡' },
  { value: 'access_point', label: 'Access Point',  icon: '📶' },
  { value: 'monitor',      label: 'Monitor',       icon: '🖵' },
  { value: 'keyboard',     label: 'Teclado',       icon: '⌨' },
  { value: 'mouse',        label: 'Mouse',         icon: '🖱' },
  { value: 'ups',          label: 'UPS',           icon: '🔋' },
  { value: 'phone',        label: 'Teléfono IP',   icon: '☎' },
  { value: 'camera',       label: 'Cámara IP',     icon: '📷' },
  { value: 'scanner',      label: 'Scanner',       icon: '📠' },
  { value: 'other',        label: 'Otro',          icon: '📦' },
];

const LIFECYCLE_OPTIONS = [
  { value: 'active',         label: 'Active' },
  { value: 'in_stock',       label: 'In Stock' },
  { value: 'in_repair',      label: 'In Repair' },
  { value: 'decommissioned', label: 'Decommissioned' },
  { value: 'stolen',         label: 'Stolen' },
  { value: 'merged',         label: 'Merged' },
];

const Field = ({ label, required, children, hint }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
      {label}{required && <span style={{ color: 'var(--red)', marginLeft: 2 }}>*</span>}
    </label>
    {children}
    {hint && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{hint}</span>}
  </div>
);

export default function AssetCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [locations, setLocations] = useState([]);

  const [form, setForm] = useState({
    name: '',
    device_type: 'workstation',
    manufacturer: '',
    model: '',
    serial_number: '',
    product_number: '',
    device_uuid: '',
    lifecycle_status: 'active',
    location_id: '',
    user_name: '',
    ip_address: '',
    mac_address: '',
    notes: '',
  });

  useEffect(() => {
    fetch(`${API_URL}/locations`, { headers })
      .then(r => r.json())
      .then(data => setLocations(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('El nombre del activo es obligatorio.'); return; }
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/assets/manual`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...form, location_name: locations.find(l => l.id === form.location_id)?.name }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error creando activo.');
      navigate(`/assets/${data.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: '24px 16px' }}>
      {/* Breadcrumb */}
      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Link to="/assets" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Inventario</Link>
        <span>›</span>
        <span>Nuevo activo físico</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <div style={{ background: '#E6F1FB', borderRadius: 10, padding: 8 }}>
          <Monitor size={20} color="var(--accent)" />
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>Nuevo activo físico</div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>Registrar dispositivo sin agente (impresora, switch, monitor, etc.)</div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ── Tipo de dispositivo ── */}
        <div className="card" style={{ padding: '20px 24px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Tipo de dispositivo
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
            {DEVICE_TYPES.map(dt => (
              <button
                key={dt.value}
                type="button"
                onClick={() => set('device_type', dt.value)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  padding: '10px 8px', borderRadius: 10, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                  border: form.device_type === dt.value ? '2px solid #0063A5' : '1.5px solid var(--border)',
                  background: form.device_type === dt.value ? '#E6F1FB' : '#fff',
                  color: form.device_type === dt.value ? 'var(--accent)' : 'var(--ink-2)',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 22 }}>{dt.icon}</span>
                {dt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Identificación ── */}
        <div className="card" style={{ padding: '20px 24px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Identificación
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Nombre / Hostname" required hint="Ej: IMPRESORA-VENTAS, SW-PISO2">
              <input
                className="form-input"
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="Ej: PRINTER-01"
                required
              />
            </Field>
            <Field label="Estado">
              <select className="form-input" value={form.lifecycle_status} onChange={e => set('lifecycle_status', e.target.value)}>
                {LIFECYCLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Fabricante" hint="Ej: HP, Canon, Cisco, Dell">
              <input className="form-input" value={form.manufacturer} onChange={e => set('manufacturer', e.target.value)} placeholder="Ej: HP" />
            </Field>
            <Field label="Modelo" hint="Ej: LaserJet Pro M404n">
              <input className="form-input" value={form.model} onChange={e => set('model', e.target.value)} placeholder="Ej: LaserJet Pro M404n" />
            </Field>
            <Field label="Número de serie">
              <input className="form-input" value={form.serial_number} onChange={e => set('serial_number', e.target.value)} placeholder="Ej: VNB3R40028" />
            </Field>
            <Field label="Número de producto / Part number">
              <input className="form-input" value={form.product_number} onChange={e => set('product_number', e.target.value)} placeholder="Ej: CF259A" />
            </Field>
          </div>
        </div>

        {/* ── Ubicación y asignación ── */}
        <div className="card" style={{ padding: '20px 24px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Ubicación y asignación
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Ubicación / Sede">
              <select className="form-input" value={form.location_id} onChange={e => set('location_id', e.target.value)}>
                <option value="">— Sin ubicación —</option>
                {locations
                  .filter(l => SERVER_TYPES.includes(form.device_type)
                    ? true                                          // server: muestra todas
                    : !CLOUD_LOCATION_NAMES.includes(l.name)       // otros: oculta cloud
                  )
                  .map(l => <option key={l.id} value={l.id}>{l.name}{l.description ? ` — ${l.description}` : ''}</option>)
                }
              </select>
            </Field>
            <Field label="Usuario / Responsable" hint="Nombre del responsable del activo">
              <input className="form-input" value={form.user_name} onChange={e => set('user_name', e.target.value)} placeholder="Ej: Juan Pérez" />
            </Field>
          </div>
        </div>

        {/* ── Red (opcional) ── */}
        <div className="card" style={{ padding: '20px 24px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Red <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--ink-3)', textTransform: 'none', letterSpacing: 0 }}>(opcional)</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Dirección IP" hint="Ej: 192.168.1.50">
              <input className="form-input" value={form.ip_address} onChange={e => set('ip_address', e.target.value)} placeholder="192.168.1.50" />
            </Field>
            <Field label="Dirección MAC" hint="Ej: AA:BB:CC:DD:EE:FF">
              <input className="form-input" value={form.mac_address} onChange={e => set('mac_address', e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" />
            </Field>
          </div>
        </div>

        {/* ── Notas ── */}
        <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Notas
          </div>
          <textarea
            className="form-input"
            rows={3}
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            placeholder="Información adicional sobre el activo..."
            style={{ resize: 'vertical' }}
          />
        </div>

        {error && (
          <div style={{ background: 'var(--red-dim)', border: '1px solid #F5C0BC', borderRadius: 8, padding: '10px 14px', color: 'var(--red)', fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Link to="/assets">
            <button type="button" className="btn btn-secondary">Cancelar</button>
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ minWidth: 140 }}>
            {saving ? 'Guardando...' : '+ Crear activo'}
          </button>
        </div>
      </form>
    </div>
  );
}
